/**
 * Operations → Under Review book.
 *
 * The Under Review book: matters that have been routed and are still
 * circulating, worst-clog-first. The surface is TIME — you scan the bars
 * (total age) top-down to find what's most backed up, then dig in to see
 * WHY. Substrate (the firm trail, decline reasons, outreach logs, case
 * summary) is reached by expanding a row and clicking through; the
 * surface SHOWS that evidence, it doesn't score it (firm-behaviour
 * scoring is Firm Performance, deferred — ADR-0036).
 *
 * Two tabs divide the line by kind — **Unsigned Leads** (cold referrals
 * still circulating) and **Transfers** (already-retained clients stuck in
 * routing) — split on `signed`, each sorted worst-clog-first. The tab is
 * the divider, so the per-row "Transfer" badge that mixed-list rows used to
 * carry is gone (it would be redundant inside a Transfers-only tab).
 *
 * Read-only. Shepherding (reroute / decline / get-info) happens in
 * Airtable / the portal; a lead re-sorts or drops off on the next poll.
 *
 * Spec: engineering/command-center/docs/port-notes/whirlpool.md (evicted 2026-08-29, #775 round 10)
 */

import { useState, type CSSProperties } from "react";
import { ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import type {
  UnderReviewLead,
  UnderReviewMisc,
  UnderReviewPlacement,
  UnderReviewSegment,
} from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { RowMeta } from "@/components/row-meta";
import {
  underReviewLeadToDetail,
  underReviewMiscToDetail,
  type CaseDetail,
} from "@/lib/case-detail";
import { cn } from "@/lib/utils";
import { fmtDate, fmtDays } from "@/lib/format";
import { useUnderReview } from "@/lib/use-under-review";

/** The divisions of the Under Review book. Unsigned leads (cold referrals circulating
 *  in routing) and Transfers (already-retained clients stuck in routing) are
 *  the circulating population, split by stakes. **Miscellaneous** is the
 *  catch-all for limbo-state matters no other surface owns (Unreviewed /
 *  Unmatched / Incomplete / Pending / Reroute / Re-present) — there so they're
 *  never invisible. */
type UnderReviewTab = "unsigned" | "transfers" | "miscellaneous";
const TAB_LABELS: Record<UnderReviewTab, string> = {
  unsigned: "Unsigned Leads",
  transfers: "Transfers",
  miscellaneous: "Miscellaneous",
};
const UNDER_REVIEW_TABS: readonly UnderReviewTab[] = [
  "unsigned",
  "transfers",
  "miscellaneous",
];

export function UnderReviewPage() {
  const underReviewBook = useUnderReview();
  // Unsigned leads are the bulk of the clog and the surface's original
  // population, so they're the default view.
  const [tab, setTab] = useState<UnderReviewTab>("unsigned");
  // The case-detail side panel is lifted to the page so it stays open
  // while you click through a lead's firms (read summary ↔ each log).
  const [detail, setDetail] = useState<CaseDetail | null>(null);
  // Client-name filter (portal parity). Filters the rows in view; the tab
  // counts stay totals (the book's distribution, not the search result).
  const [query, setQuery] = useState("");

  if (underReviewBook.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (underReviewBook.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="under-review-error">
        Couldn't load the Under Review book. It will retry automatically.
      </div>
    );
  }

  // The composer ships `leads` worst-clog-first and `miscellaneous`
  // longest-waiting-first. Partitioning `leads` by `signed` preserves order
  // within each tab, so the lists stay sorted for free.
  const leads = underReviewBook.data?.leads ?? [];
  const miscellaneous = underReviewBook.data?.miscellaneous ?? [];
  const unsigned = leads.filter((l) => !l.signed);
  const transfers = leads.filter((l) => l.signed);
  const tabCount: Record<UnderReviewTab, number> = {
    unsigned: unsigned.length,
    transfers: transfers.length,
    miscellaneous: miscellaneous.length,
  };
  const surfaceEmpty = leads.length === 0 && miscellaneous.length === 0;
  // Name-filtered views for the rows in the active tab.
  const fMisc = miscellaneous.filter((m) => matchesQuery(m.name, query));
  const fUnsigned = unsigned.filter((l) => matchesQuery(l.name, query));
  const fTransfers = transfers.filter((l) => matchesQuery(l.name, query));
  const fLeads = tab === "unsigned" ? fUnsigned : fTransfers;
  const searching = query.trim() !== "";

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="under-review">
      <BookHeading phaseKey="under-review">
        <span className="text-sm text-plane-muted" data-testid="under-review-count">
          {leads.length === 0 ? "" : `${leads.length} circulating`}
        </span>
      </BookHeading>

      {surfaceEmpty ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="under-review-empty"
        >
          <p className="text-lg text-plane-muted">Nothing circling.</p>
        </div>
      ) : (
        <>
          {/* Tabs — the circulating split (Unsigned / Transfers) plus the
              Miscellaneous catch-all. The count on each is the glance. */}
          <div
            className="mb-4 flex flex-wrap gap-1 border-b"
            role="tablist"
            data-testid="under-review-tabs"
          >
            {UNDER_REVIEW_TABS.map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={t === tab}
                onClick={() => setTab(t)}
                className={cn(
                  "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
                  t === tab
                    ? "border-active-edge font-medium text-plane-foreground"
                    : "border-transparent text-plane-muted hover:text-plane-foreground",
                )}
                data-testid={`under-review-tab-${t}`}
              >
                {TAB_LABELS[t]}
                <span className="ml-2 tabular-nums text-plane-muted">
                  {tabCount[t]}
                </span>
              </button>
            ))}
          </div>

          <SearchBar value={query} onChange={setQuery} testId="under-review-search" />

          {tab === "miscellaneous" ? (
            fMisc.length === 0 ? (
              <TabEmpty
                message={
                  searching ? "No matches." : "Nothing in Miscellaneous."
                }
              />
            ) : (
              <ul className="flex flex-col gap-2">
                {fMisc.map((m) => (
                  <MiscRow
                    key={m.id}
                    misc={m}
                    onOpenSummary={() => setDetail(underReviewMiscToDetail(m))}
                  />
                ))}
              </ul>
            )
          ) : fLeads.length === 0 ? (
            <TabEmpty
              message={
                searching
                  ? "No matches."
                  : `No ${TAB_LABELS[tab].toLowerCase()} circling.`
              }
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {fLeads.map((lead) => (
                <UnderReviewRow
                  key={lead.id}
                  lead={lead}
                  onOpenSummary={() => setDetail(underReviewLeadToDetail(lead))}
                />
              ))}
            </ul>
          )}
        </>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="under-review-summary-panel"
        />
      ) : null}
    </section>
  );
}

/** Per-tab empty placeholder — used when the surface has matters but the
 *  selected tab doesn't. */
function TabEmpty({ message }: { message: string }) {
  return (
    <div
      className="flex min-h-[40vh] items-center justify-center text-center"
      data-testid="under-review-tab-empty"
    >
      <p className="text-lg text-plane-muted">{message}</p>
    </div>
  );
}

/** One Miscellaneous row — a limbo-state matter with no routing trail. Shows
 *  who, the blocker (its Lead Status, as a badge), the OF, and how long it's
 *  waited; a Transfer is marked. No custody bar / trajectory (there's no
 *  routing to show); the case-summary side panel is one click away. */
export function MiscRow({
  misc,
  onOpenSummary,
  embedded = false,
}: {
  misc: UnderReviewMisc;
  onOpenSummary: () => void;
  /** Rendered inside The Surface's attention-card — drop the card shell so it
   *  isn't a card nested in a card. (No severity accent either way: a limbo
   *  matter's signal is its status badge, not an age band.) */
  embedded?: boolean;
}) {
  return (
    <li
      className={cn(
        "relative",
        !embedded && "rounded-xl border bg-card text-card-foreground shadow-sm",
      )}
      data-testid="under-review-misc-row"
    >
      <div className="flex w-full items-center gap-3 px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-medium">{misc.name}</span>
          <Badge
            variant={miscStatusVariant(misc.status)}
            data-testid="under-review-misc-status"
          >
            {misc.status ?? "—"}
          </Badge>
          {misc.signed ? <Badge variant="blue">Transfer</Badge> : null}
          {misc.originatingFirmName ? (
            <span className="text-xs text-muted-foreground">
              · {misc.originatingFirmName}
            </span>
          ) : null}
          {misc.caseType ? (
            <span className="text-xs text-muted-foreground">· {misc.caseType}</span>
          ) : null}
          {misc.incidentState ? (
            <span className="text-xs text-muted-foreground">
              · {misc.incidentState}
            </span>
          ) : null}
        </div>

        {/* How long it's sat in limbo — the worst-first axis. */}
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums"
            data-testid="under-review-misc-age"
          >
            {fmtDays(misc.ageDays)}
          </span>
          <span className="block text-[11px] text-muted-foreground">waiting</span>
        </span>

        <SurfaceTrigger leadId={misc.id} />

        <button
          type="button"
          onClick={onOpenSummary}
          className="shrink-0 text-xs font-medium text-interactive hover:underline"
          data-testid="under-review-misc-summary"
        >
          Case summary →
        </button>
      </div>
    </li>
  );
}

/** Badge colour for a Miscellaneous blocker. Amber = needs operator action to
 *  unblock (no firm / missing data); blue = mid re-routing; slate = freshly in
 *  the queue (awaiting routing). */
function miscStatusVariant(
  status: string | null,
): "amber" | "blue" | "slate" | "muted" {
  switch (status) {
    case "Unmatched":
    case "Incomplete":
      return "amber";
    case "Reroute":
    case "Re-present":
      return "blue";
    case "Unreviewed":
    case "Pending":
      return "slate";
    default:
      return "muted";
  }
}

export function UnderReviewRow({
  lead,
  onOpenSummary,
  embedded = false,
}: {
  lead: UnderReviewLead;
  onOpenSummary: () => void;
  /** Rendered inside another card — The Surface's attention-card, which already
   *  owns the chrome and paints the phase (left) accent. When true the row drops
   *  its own card shell and severity accent so it doesn't nest a card in a card
   *  (and doesn't stack two left rules). */
  embedded?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <li
      className={cn(
        "relative",
        // Standalone (book page): the portal card shape — 12px radius, soft
        // lift, white surface — with a saturated left accent rule by TOTAL age.
        // This replaces the old whole-card pastel wash (the "90s slop"): same
        // severity thresholds, refined to a crisp edge on white.
        !embedded && "rounded-xl border bg-card text-card-foreground shadow-sm",
      )}
      style={
        embedded
          ? undefined
          : {
              borderLeftWidth: 4,
              borderLeftColor: rowAccentHex(lead.totalAgeDays),
            }
      }
      data-testid="under-review-row"
    >
      <SurfaceTrigger
        leadId={lead.id}
        className="absolute right-2 top-2 z-10"
      />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full flex-col gap-1.5 px-4 py-3 pr-20 text-left"
        aria-expanded={expanded}
        data-testid="under-review-row-toggle"
      >
        {/* Row 1 — name → trajectory. Reads left-to-right: lead, then each
            firm it's passed through (oldest→newest), so the last hop is who
            holds it now and the chain length is the depth. Grouped beside the
            name so the eye doesn't bounce; the named hops sit right above the
            bar's matching time-segments. */}
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <span className="text-sm font-medium">{lead.name}</span>
          {/* The Lead/Transfer distinction is carried by the page tabs now,
              not a per-row badge — a Transfers-only tab makes the marker
              redundant. */}
          <TrajectoryChain lead={lead} />
        </div>

        {/* Row 2 — orientation (the portal's at-a-glance columns: referred-by ·
            case type · jurisdiction · received), ported onto the card. The
            outreach "latest update" read deliberately stays in the substrate
            below: this book's glance is pure TIME (see file header), so the
            is-it-being-worked evidence costs the expand click. */}
        <RowMeta
          className="pl-6"
          testId="under-review-meta"
          originatingFirmName={lead.originatingFirmName}
          caseType={lead.caseType}
          incidentState={lead.incidentState}
          received={lead.creationDate ?? lead.createdTime}
        />

        {/* Row 3 — the custody timeline (SURFACE). Fixed 0–30d scale with
            5-day ticks; cut into firm + LegalFlare stretches, each coloured
            by ITS OWN days. Long stretch = fat chunk turns red on sight. */}
        <div className="flex items-center gap-3 pl-6">
          <CustodyBar segments={lead.segments} totalAgeDays={lead.totalAgeDays} />
          <span
            className={cn(
              "w-20 shrink-0 text-right text-sm font-semibold tabular-nums",
              rowTotalTextClass(lead.totalAgeDays),
            )}
          >
            {fmtTotal(lead.totalAgeDays)} total
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="border-t px-4 py-3 pl-10" data-testid="under-review-detail">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Trajectory · {lead.trail.length}{" "}
              {lead.trail.length === 1 ? "firm" : "firms"}
            </span>
            <button
              type="button"
              onClick={onOpenSummary}
              className="text-xs font-medium text-interactive hover:underline"
              data-testid="under-review-open-summary"
            >
              Case summary →
            </button>
          </div>
          <ol className="flex flex-col gap-1.5">
            {lead.trail.map((p, i) => {
              // The current holder is the last (newest) stretch. The "is it
              // being worked?" read lives HERE, in the substrate — the glance
              // stays pure time; verifying "old, but actively worked" costs
              // the click that opened this trail. Omitted on a declined-stuck
              // lead: no firm holds it now (the clog is LF's idle reroute).
              const isCurrent = i === lead.trail.length - 1;
              const showOutreach = isCurrent && !lead.declinedStuck;
              const hasDays = showOutreach && lead.lastOutreachDays !== null;
              const outreachLabel = !showOutreach
                ? null
                : hasDays
                  ? `worked ${fmtDays(lead.lastOutreachDays!)} ago`
                  : "no outreach logged";
              // ≤5 days reads green (recently worked), 6+ red (going quiet) —
              // the line Chris set by eye. Absence ("no outreach logged")
              // stays neutral. Two bands, not the bar's three.
              const outreachClass = hasDays
                ? outreachToneClass(lead.lastOutreachDays!)
                : "text-muted-foreground";
              return (
                <TrailItem
                  key={i}
                  placement={p}
                  outreachLabel={outreachLabel}
                  outreachClass={outreachClass}
                />
              );
            })}
          </ol>
        </div>
      ) : null}
    </li>
  );
}

/** The inline trajectory beside the lead name — one hop per custody STRETCH,
 *  oldest→newest, a named mirror of the bar below it. Every stretch is named,
 *  reroute / idle gaps included: a stretch where no firm held the lead reads
 *  "LegalFlare" — if WE sat on it, it's on the record (accountability; the bar
 *  shows how long). Firm names are coloured to their segment's band (≤5 green
 *  / 6–10 yellow / 11+ red) so name and segment tie together — the clog reads
 *  red in both places; LegalFlare gaps read amber (us, not a firm). The
 *  current (last) hop is emphasised and prints its leg's days beside it,
 *  straight from the same segment the bar's final stretch is drawn from, so
 *  the number can never disagree with the bar. (Building from segments — not
 *  the placement list + currentPositionAgeDays — is what killed the mismatch
 *  that under-reported a 23-day-red bar as "2d".) */
function TrajectoryChain({ lead }: { lead: UnderReviewLead }) {
  const hops = lead.segments.map((s) =>
    s.holder === "legalflare"
      ? { name: "LegalFlare", colour: "text-warning", days: s.days }
      : {
          name: s.firmName ?? "Unknown firm",
          colour: segTextClass(s.days),
          days: s.days,
        },
  );
  if (hops.length === 0) return null;

  return (
    <span
      className="flex flex-wrap items-center gap-x-1 text-xs"
      data-testid="under-review-trajectory"
    >
      {hops.map((hop, i) => {
        const isCurrent = i === hops.length - 1;
        return (
          <span key={i} className="flex items-center gap-x-1">
            <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground opacity-60" />
            <span className={cn(hop.colour, isCurrent && "font-medium")}>
              {hop.name}
            </span>
            {/* Days on the CURRENT leg, beside the current holder so "who has
                it" and "for how long" read together — from this segment, the
                same datum the bar's final stretch is drawn from. */}
            {isCurrent ? (
              <span
                className={cn("font-medium tabular-nums", hop.colour)}
                data-testid="under-review-current-days"
              >
                {fmtDays(hop.days)}
              </span>
            ) : null}
          </span>
        );
      })}
    </span>
  );
}

/** One firm's leg of the journey. Collapsed: firm + decision + when sent.
 *  Expanded: the evidence — decline reason and outreach log. The current
 *  holder also carries the "worked Nd ago" read (`outreachLabel`). */
function TrailItem({
  placement,
  outreachLabel,
  outreachClass,
}: {
  placement: UnderReviewPlacement;
  /** "worked Nd ago" / "no outreach logged" for the CURRENT holder — the
   *  is-it-being-worked read, shown in the substrate so verifying it costs a
   *  click. null for non-current firms and declined-stuck leads. */
  outreachLabel?: string | null;
  /** Colour for `outreachLabel`: green ≤5d, red 6+d, muted when undated. */
  outreachClass?: string;
}) {
  const [open, setOpen] = useState(false);
  const hasEvidence =
    (placement.declineReason !== null && placement.declineReason.length > 0) ||
    (placement.outreachLog !== null && placement.outreachLog.length > 0);

  return (
    <li className="rounded border bg-background" data-testid="under-review-trail-item">
      <button
        type="button"
        onClick={() => hasEvidence && setOpen((v) => !v)}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
          hasEvidence ? "cursor-pointer" : "cursor-default",
        )}
        aria-expanded={hasEvidence ? open : undefined}
      >
        {hasEvidence ? (
          open ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        <span className="font-medium">{placement.firmName ?? "Unknown firm"}</span>
        {placement.decision ? (
          <Badge variant={decisionVariant(placement.decision)}>
            {placement.decision}
          </Badge>
        ) : null}
        {outreachLabel ? (
          <span
            className={cn("text-xs italic", outreachClass ?? "text-muted-foreground")}
            data-testid="under-review-outreach"
          >
            {outreachLabel}
          </span>
        ) : null}
        {placement.dateSent ? (
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            sent {fmtDate(placement.dateSent)}
          </span>
        ) : (
          <span className="ml-auto shrink-0 text-xs text-muted-foreground">
            not sent yet
          </span>
        )}
      </button>

      {open && hasEvidence ? (
        <div className="border-t px-3 py-2 pl-8 text-xs">
          {placement.declineReason ? (
            <div className="mb-2" data-testid="under-review-decline-reason">
              <span className="font-medium text-error">Decline reason: </span>
              <span className="whitespace-pre-wrap text-foreground">
                {placement.declineReason}
              </span>
            </div>
          ) : null}
          {placement.outreachLog ? (
            <div data-testid="under-review-outreach-log">
              <span className="font-medium text-muted-foreground">
                Outreach log:
              </span>
              <p className="mt-0.5 whitespace-pre-wrap text-muted-foreground">
                {placement.outreachLog}
              </p>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

/** Placement Decision → badge colour. Red = firm gone (terminal),
 *  blue = actively reviewing, slate = pre-review. */
function decisionVariant(
  decision: string,
): "red" | "amber" | "blue" | "slate" | "muted" {
  switch (decision) {
    case "Declined":
    case "Pulled":
      return "red";
    case "Reviewing":
      return "blue";
    case "Sent":
    case "Assigned":
      return "slate";
    default:
      return "muted";
  }
}

/** Fixed scale: the bar runs 0–30 days. Past 30 it's full ("30+"); beyond
 *  that the exact age no longer changes the decision. Ticks every 5 days. */
const BAR_CAP_DAYS = 30;
const BAR_TICKS = [5, 10, 15, 20, 25];

/** The custody timeline bar. Segments sum to the total age and lay out on a
 *  fixed 0–30d ruler: under the cap they fill total/30 of the track; over
 *  the cap the bar is full and segments share it proportionally. Colour is
 *  always by a segment's REAL days, never its (possibly compressed) width. */
function CustodyBar({
  segments,
  totalAgeDays,
}: {
  segments: UnderReviewSegment[];
  totalAgeDays: number;
}) {
  const denom = totalAgeDays > BAR_CAP_DAYS ? totalAgeDays : BAR_CAP_DAYS;

  return (
    <div
      className="relative h-4 flex-1 overflow-hidden rounded bg-muted"
      data-testid="under-review-bar"
    >
      <div className="flex h-full w-full">
        {segments.map((s, i) => (
          <div
            key={i}
            className="h-full"
            style={segStyle(s, (s.days / denom) * 100)}
            title={segTitle(s)}
            data-testid="under-review-bar-segment"
          />
        ))}
      </div>
      {/* 5-day tick marks across the full ruler. */}
      {BAR_TICKS.map((d) => (
        <span
          key={d}
          className="pointer-events-none absolute top-0 h-full w-px bg-white/60"
          style={{ left: `${(d / BAR_CAP_DAYS) * 100}%` }}
        />
      ))}
    </div>
  );
}

/** Severity band by a stretch's OWN days: ≤5 green, 6–10 yellow, 11+ red.
 *  Single-sourced so the bar fill and the trajectory-name colour stay in
 *  lockstep — a firm's segment and its name always read the same band. */
type Band = "green" | "yellow" | "red";
function segBand(days: number): Band {
  if (days <= 5) return "green";
  if (days <= 10) return "yellow";
  return "red";
}

/** Bar fill (solid block) — the saturated severity fill, via theme token so the
 *  dark toggle reaches it (Q-DARK). Severity = PRIORITY; the only green/amber/red
 *  in the system. */
const BAND_HEX: Record<Band, string> = {
  green: "hsl(var(--sev-green))",
  yellow: "hsl(var(--sev-amber))",
  red: "hsl(var(--sev-red))",
};
function segBandHex(days: number): string {
  return BAND_HEX[segBand(days)];
}

/** Trajectory name (text) — the darker -700 shade of the same hue, so it
 *  ties to the bar segment while staying legible (the -500 fills, yellow
 *  especially, are too light for text). Null days → muted (no segment). */
const BAND_TEXT: Record<Band, string> = {
  green: "text-[hsl(var(--sev-green-fg))]",
  yellow: "text-[hsl(var(--sev-amber-fg))]",
  red: "text-[hsl(var(--sev-red-fg))]",
};
function segTextClass(days: number | null): string {
  return days === null ? "text-muted-foreground" : BAND_TEXT[segBand(days)];
}

/** One segment's inline style. A firm stretch is a solid fill; a LegalFlare
 *  stretch is diagonal stripes of the same band colour, so "our" idle time
 *  reads as distinct from a firm's. */
function segStyle(s: UnderReviewSegment, widthPct: number): CSSProperties {
  const c = segBandHex(s.days);
  if (s.holder === "legalflare") {
    // Faded stripe via color-mix (the token is hsl(var(...)), so hex-alpha concat
    // does not apply); 60% keeps "our" idle time visibly lighter than a firm's.
    const faded = `color-mix(in srgb, ${c} 60%, transparent)`;
    return {
      width: `${widthPct}%`,
      background: `repeating-linear-gradient(45deg, ${c} 0, ${c} 5px, ${faded} 5px, ${faded} 10px)`,
    };
  }
  return { width: `${widthPct}%`, backgroundColor: c };
}

/** Hover label for a segment — who held it, for how long. */
function segTitle(s: UnderReviewSegment): string {
  const who =
    s.holder === "legalflare"
      ? "LegalFlare (rerouting)"
      : (s.firmName ?? "Unknown firm");
  return `${who} · ${fmtDays(s.days)}`;
}

/** Severity band by TOTAL age: ≤10 green, 11–20 yellow, 21+ red — the overall
 *  "how backed up is this" read, distinct from the bar's per-stretch bands.
 *  Replaces the old whole-card pastel wash (the "90s slop"): the band now drives
 *  a crisp left accent rule + the total-age figure's colour against a white
 *  card. Same thresholds (Chris-set), refined rendering. */
function rowBand(totalAgeDays: number): Band {
  if (totalAgeDays <= 10) return "green";
  if (totalAgeDays <= 20) return "yellow";
  return "red";
}

/** Left accent-rule hex for the row, by total-age band — the saturated -500
 *  fill shade, reused from the bar so edge and bar read the same band. */
function rowAccentHex(totalAgeDays: number): string {
  return BAND_HEX[rowBand(totalAgeDays)];
}

/** Total-age figure colour, by band — the darker -700 text shade, so the
 *  number ties to its left accent and the bar below it. */
function rowTotalTextClass(totalAgeDays: number): string {
  return BAND_TEXT[rowBand(totalAgeDays)];
}

/** Colour for the current firm's outreach recency: ≤5 days green (recently
 *  worked), 6+ red (going quiet). Thresholds on the DISPLAYED whole-day
 *  number (same rounding as `fmtDays`) so "5d" is always green and "6d"
 *  always red. A two-band call Chris set by eye — deliberately not the
 *  bar's three-band severity. */
function outreachToneClass(days: number): string {
  return Math.round(days) <= 5
    ? "text-[hsl(var(--sev-green-fg))]"
    : "text-[hsl(var(--sev-red-fg))]";
}

/** Total-age label. Past the 30-day cap it reads "30+d" — beyond that the
 *  exact number doesn't change the call. */
function fmtTotal(days: number): string {
  if (days > BAR_CAP_DAYS) return `${BAR_CAP_DAYS}+d`;
  return days < 1 ? "<1d" : `${Math.round(days)}d`;
}

